import mixpanel from 'mixpanel-browser';
import { datadogRum } from '@datadog/browser-rum';
import { merge } from 'lodash';
import { getEnvironment, store } from './index';

function setupRUM() {
    datadogRum.init({
        applicationId: process.env.DATA_DOG_APPLICATION_ID,
        clientToken: process.env.DATA_DOG_CLIENT_TOKEN,
        site: process.env.DATA_DOG_SITE,
        service: process.env.DATA_DOG_SERVICE_NAME,
        env: getEnvironment(),
        sampleRate: 1,
        sessionReplaySampleRate: 1,
        trackInteractions: true,
        trackResources: true,
        trackLongTasks: true,
        defaultPrivacyLevel: 'mask-user-input',
        replaySampleRate: 1,
        premiumSampleRate: 1
    });

    datadogRum.startSessionReplayRecording();
}

function logMixpanel(event, data = null) {
    console.groupCollapsed(`MIXPANEL_DEBUG :: ${ event }`);
    if (data) {
        console.dir('data ::', data);
    }
    console.groupEnd();
}

function setUpMixpanel() {
    const mixpanelToken = process.env.MIXPANEL_TOKEN_FRONTEND;

    window.ATTUNED_USE_PROXIES = process.env.ATTUNED_USE_PROXIES;
    window.HAS_MIXPANEL_LOADED = false;

    if (!mixpanelToken) {
        console.error('Unable to initialize Mixpanel: token not found');
        return;
    }

    const config = {
        loaded: () => window.HAS_MIXPANEL_LOADED = true
    };

    if (window.ATTUNED_USE_PROXIES && window.ATTUNED_USE_PROXIES === '1') {
        config.api_host = '/mp';
    }

    mixpanel.init(mixpanelToken, config);

    if (process.env.NODE_ENV === 'development') {
        logMixpanel('SETUP');
    }
}

function getMixpanelPropertiesFromState() {
    const state = store.getState();

    return !state
        ? {}
        : {
            accountId: state.respondentNameReducer.get('accountId'),
            email: state.respondentNameReducer.get('respondent_email'),
            surveyId: state.respondentNameReducer.get('surveyId'),
            firstName: state.respondentNameReducer.get('firstName'),
            lastName: state.respondentNameReducer.get('lastName'),
            language: state.changeLanguageReducer.get('language')
        };
}

export function trackMixpanelEvent({
    eventName,
    cb = null,
    immediate = false,
    properties = {}
}) {
    if (!window.HAS_MIXPANEL_LOADED) {
        console.warn(`Failed to track event ${ eventName }: Mixpanel is not enabled`);
        return null;
    }

    const data = merge({},
        getMixpanelPropertiesFromState(),
        properties
    );

    if (process.env.NODE_ENV === 'development') {
        logMixpanel(eventName, data);
    }

    if (cb) {
        return () => {
            mixpanel.track(eventName, data, { send_immediately: immediate });
            cb();
        };
    }

    return mixpanel.track(eventName, data, { send_immediately: immediate });
}

export function initiateMixpanel({
    email,
    accountId,
    firstName,
    lastName
}) {
    if (!window.HAS_MIXPANEL_LOADED) {
        console.warn('Failed to initialized Mixpanel: Mixpanel is not enabled');
        return;
    }

    if (!email || !accountId) return;

    const distinctId = `${ email }_${ accountId }`;

    mixpanel.identify(distinctId);
    mixpanel.alias(distinctId, email);

    mixpanel.people.set({
        $email: email,
        $name: `${ firstName } ${ lastName }`,
        $first_name: firstName,
        $last_name: lastName
    });

    if (process.env.NODE_ENV === 'development') {
        logMixpanel('INIT', {
            email,
            accountId,
            firstName,
            lastName,
            distinctId
        });
    }
}

export function initThirdPartyServices() {
    setUpMixpanel();

    if (getEnvironment() !== 'dev') {
        setupRUM();
    }
}
